import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { isTokenExpired } from '@/utils/jwt';
import { useEffect, useState } from 'react';

interface ProtectedRouteProps {
  requireRoot?: boolean;
  requireRootOrAdmin?: boolean;
  requireSuperRoot?: boolean;
}

export function ProtectedRoute({ requireRoot = false, requireRootOrAdmin = false, requireSuperRoot = false }: ProtectedRouteProps) {
  const { token, user, isAuthenticated, isRoot, isAdmin, isSuperRoot } = useAuthStore();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  // Restore token and user from localStorage on mount if not in store
  useEffect(() => {
    const checkAuth = () => {
      const storedToken = localStorage.getItem('auth_token');
      const storedRefreshToken = localStorage.getItem('refresh_token');
      const state = useAuthStore.getState();
      
      // If token exists in localStorage but not in store, restore it
      if ((!state.token) && storedToken) {
        state.updateToken(storedToken);
        if (storedRefreshToken) {
          state.setRefreshToken(storedRefreshToken);
        }
      }

      // Check if we have both token and user
      const currentToken = state.token || storedToken;
      const currentUser = state.user;

      // If we have a token but no user, wait a bit for Zustand persist to rehydrate
      if (currentToken && !currentUser) {
        // Give Zustand persist middleware time to rehydrate user from localStorage
        const timer = setTimeout(() => {
          setIsChecking(false);
        }, 150);
        return () => clearTimeout(timer);
      }

      setIsChecking(false);
    };

    checkAuth();
  }, [token, user]);

  // Check authentication directly from store values
  // Use isAuthenticated() which handles token validation, but also check store directly
  const currentToken = token || localStorage.getItem('auth_token');
  const currentUser = user || useAuthStore.getState().user;
  const authenticated = currentToken && currentUser && !isTokenExpired(currentToken);

  // Show loading state while checking
  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <span className="material-icons-outlined animate-spin text-primary text-4xl mb-4">progress_activity</span>
          <p className="text-slate-400">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  // ROLE_ROOT / ROLE_ADMIN: l'interface admin ne dépend pas d'un projet.
  // On évite de les laisser naviguer sur l'UI user (/dashboard*).
  if (location.pathname.startsWith('/dashboard')) {
    if (isRoot()) return <Navigate to="/admin/dashboard" replace />;
    if (isAdmin() && !isRoot()) return <Navigate to="/admin/dashboard" replace />;
  }

  if (requireRoot && !isRoot()) {
    return <Navigate to="/" replace />;
  }

  if (requireRootOrAdmin && !isRoot() && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  if (requireSuperRoot && !isSuperRoot()) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
